import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Plus, Eye, EyeOff } from 'lucide-react';
import { Quote } from '../types';

interface BookingCalendarProps { quotes: Quote[]; }

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function BookingCalendar({ quotes }: BookingCalendarProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [showInternal, setShowInternal] = useState(true);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Build booked dates map
  const bookedDates: Record<string, { event: string; customer: string; tier: string }[]> = {};
  quotes.forEach(q => {
    q.packageItems?.forEach(pkg => {
      pkg.eventDates?.forEach(ed => {
        if (!ed.date) return;
        const d = new Date(ed.date);
        if (d.getFullYear() !== year || d.getMonth() !== month) return;
        const key = d.getDate().toString();
        if (!bookedDates[key]) bookedDates[key] = [];
        bookedDates[key].push({ event: q.eventName, customer: q.customerName, tier: pkg.qualityTier });
      });
    });
  });

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  const TIER_BG: Record<string, string> = { Best: 'rgba(46,125,50,0.7)', Luxe: 'rgba(21,101,192,0.7)', Elite: 'rgba(106,27,154,0.7)', General: 'rgba(160,82,45,0.7)' };

  const selectedBookings = selectedDay ? bookedDates[selectedDay.toString()] || [] : [];

  return (
    <div style={{ padding: 28, maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Calendar size={22} color="var(--gold)" />
          <div>
            <h1>Booking Calendar</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 2 }}>Internal availability view</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className={`btn ${showInternal ? 'btn-teal' : 'btn-secondary'}`} onClick={() => setShowInternal(!showInternal)}>
            {showInternal ? <Eye size={14} /> : <EyeOff size={14} />}
            Internal view
          </button>
        </div>
      </div>

      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <button className="btn btn-secondary btn-icon" onClick={prevMonth}><ChevronLeft size={16} /></button>
        <h2 style={{ fontSize: 20, minWidth: 200, textAlign: 'center' }}>{MONTHS[month]} {year}</h2>
        <button className="btn btn-secondary btn-icon" onClick={nextMonth}><ChevronRight size={16} /></button>
      </div>

      {/* Calendar grid */}
      <div className="card" style={{ padding: 16 }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 8 }}>
          {DAYS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', padding: '4px 0', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {/* Empty cells for first week */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} style={{ minHeight: 80 }} />
          ))}
          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const bookings = bookedDates[day.toString()] || [];
            const isToday = isCurrentMonth && day === today;
            const isSelected = selectedDay === day;
            const hasBooking = bookings.length > 0;

            return (
              <div key={day} onClick={() => setSelectedDay(isSelected ? null : day)}
                style={{
                  minHeight: 80, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                  background: isSelected ? 'rgba(201,168,76,0.1)' : hasBooking ? 'rgba(15,110,86,0.06)' : 'var(--bg-hover)',
                  border: `1px solid ${isSelected ? 'var(--gold)' : isToday ? 'var(--teal)' : hasBooking ? 'rgba(15,110,86,0.3)' : 'var(--border)'}`,
                }}>
                <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--teal-light)' : 'var(--text-primary)', marginBottom: 4 }}>
                  {day}
                  {isToday && <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: 'var(--teal)', marginLeft: 4, verticalAlign: 'middle' }} />}
                </div>
                {bookings.slice(0, 2).map((b, bi) => (
                  <div key={bi} style={{ fontSize: 10, padding: '2px 4px', borderRadius: 3, background: TIER_BG[b.tier] || 'var(--bg-card)', color: 'white', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.customer}
                  </div>
                ))}
                {bookings.length > 2 && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{bookings.length - 2} more</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="card" style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 12 }}>{MONTHS[month]} {selectedDay}, {year}</h4>
          {selectedBookings.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No bookings on this day.</p>
          ) : (
            selectedBookings.map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--bg-hover)', borderRadius: 'var(--radius-sm)', marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: TIER_BG[b.tier]?.replace('0.7', '1') || 'var(--gold)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{b.event}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{b.customer}</div>
                </div>
                <span className={`badge ${b.tier === 'Best' ? 'badge-best' : b.tier === 'Luxe' ? 'badge-luxe' : b.tier === 'Elite' ? 'badge-elite' : 'badge-amber'}`}>{b.tier}</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        {[{ label: 'Best', color: 'rgba(46,125,50,0.7)' }, { label: 'Luxe', color: 'rgba(21,101,192,0.7)' }, { label: 'Elite', color: 'rgba(106,27,154,0.7)' }, { label: 'General Planner', color: 'rgba(160,82,45,0.7)' }].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
}
